import { useState } from "react";
import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import { IconAlertTriangle, IconInfoCircle, IconTrash, IconBolt } from "@tabler/icons";

import { ModalBody } from "./ModalBody";
import { Tooltip } from "../Tooltip";
import { Checkbox } from "../Checkbox";
import { UpdateJourney } from "../../modules/Campaigns/graphql/mutations/journey";

export const AutomationReferenceWarning = ({ resourceLabel, willDeactivate = [], mayBreak = [], acknowledged, setAcknowledged }) => {
  const hasWillDeactivate = willDeactivate.length > 0;
  const hasMayBreak = mayBreak.length > 0;

  return (
    <>
      {hasWillDeactivate && (
        <div className="pt-[24px] pb-[20px]">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-danger text-white shrink-0">!</span>
            <span className="pl-[10px] text-[15px] font-semibold text-danger">
              <FormattedMessage
                id="component.automationReferenceModal.willDeactivateTitle"
                defaultMessage="{count, plural, one {# automation} other {# automations}} will be deactivated."
                values={{ count: willDeactivate.length }}
              />
            </span>
            <Tooltip
              action="default"
              position="top"
              fixedToBody
              topGap={12}
              label={
                <div style={{ maxWidth: "300px" }}>
                  <FormattedMessage
                    id="component.automationReferenceModal.willDeactivateWarning"
                    defaultMessage="These automations use this {resourceLabel} as their trigger. Deleting this {resourceLabel} will deactivate them."
                    values={{ resourceLabel }}
                  />
                </div>
              }
            >
              <IconInfoCircle size={16} className="text-gray-800 cursor-pointer" />
            </Tooltip>
          </div>
          <div className="mt-[12px] border border-gray-400 rounded-[8px] divide-y divide-gray-400 ml-[42px] mr-6">
            {willDeactivate.map(({ id, name, hasBegun }) => (
              <div key={id} className="flex items-stretch gap-2 px-[10px] py-[10px] text-sm text-gray-900">
                <div className="flex items-center pr-[10px] border-r border-gray-400">
                  <IconBolt size={16} className="text-gray-800 shrink-0" />
                </div>
                <span className="flex items-center justify-between flex-1 min-w-0 gap-2">
                  <span className="truncate min-w-0 max-w-[30ch]" title={name}>{name}</span>
                {hasBegun && (
                  <Tooltip
                    action="danger"
                    position="top"
                    fixedToBody
                    topGap={12}
                    label={
                      <div style={{ maxWidth: "300px" }}>
                        <FormattedMessage
                          id="component.automationReferenceModal.lockedTooltip"
                          defaultMessage="This automation's trigger is locked because it has already run. It will be permanently deactivated."
                        />
                      </div>
                    }
                  >
                    <IconAlertTriangle size={20} className="text-danger cursor-pointer" />
                  </Tooltip>
                )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasMayBreak && (
        <div className={`${hasWillDeactivate ? 'pt-[8px] pb-[20px]' : 'pt-[24px]'}`}>
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white shrink-0">
              <IconInfoCircle size={16} />
            </span>
            <span className="pl-[10px] text-[15px] font-semibold text-primary">
              <FormattedMessage
                id="component.automationReferenceModal.mayBreakTitle"
                defaultMessage="{count, plural, one {# automation needs} other {# automations need}} review."
                values={{ count: mayBreak.length }}
              />
            </span>
            <Tooltip
              action="default"
              position="top"
              label={
                <div style={{ maxWidth: "300px" }}>
                  <FormattedMessage
                    id="component.automationReferenceModal.mayBreakWarning"
                    defaultMessage="These automations use this {resourceLabel} in an action or condition (or as one of several trigger options). We recommend updating them first — if you proceed, they may stop working as expected."
                    values={{ resourceLabel }}
                  />
                </div>
              }
            >
              <IconInfoCircle size={16} className="text-gray-800 cursor-pointer" />
            </Tooltip>
          </div>
          <div className="mt-[12px] border border-gray-400 rounded-[8px] divide-y divide-gray-400 ml-[42px] mr-6">
            {mayBreak.map(({ id, name }) => (
              <div key={id} className="flex items-stretch gap-2 px-[10px] py-[10px] text-sm text-gray-900">
                <div className="flex items-center pr-[10px] border-r border-gray-400">
                  <IconBolt size={16} className="text-gray-800 shrink-0" />
                </div>
                <span className="self-center truncate min-w-0 flex-1 max-w-[30ch]" title={name}>{name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasWillDeactivate && (
        <div>
          <Checkbox
            checked={acknowledged}
            onClick={() => setAcknowledged(!acknowledged)}
            label={<FormattedMessage id="component.automationReferenceModal.acknowledge" defaultMessage="I understand the impact of deleting this {resourceLabel}." values={{ resourceLabel }} />}
            ellipsis={false}
          />
        </div>
      )}
    </>
  );
};

export const AutomationReferenceModal = ({ resourceLabel, willDeactivate = [], mayBreak = [], onDelete, handleClose }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updateJourney] = useMutation(UpdateJourney);

  const hasWillDeactivate = willDeactivate.length > 0;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      if (hasWillDeactivate) {
        await Promise.all(willDeactivate.map(({ id }) => updateJourney({ variables: { id, active: false } })));
      }
      await onDelete();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBody
      cn="pt-[30px] max-h-[calc(100vh-200px)] overflow-y-auto"
      footerMargin="mt-[24px]"
      handleClose={handleClose}
      closeOnSubmit={false}
      icon={<IconTrash />}
      action="danger"
      iconInline
      title={<FormattedMessage id="component.automationReferenceModal.title" defaultMessage="Delete {resourceLabel}" values={{ resourceLabel }} />}
      description={
        <div className="pt-2">
          <div className="text-[15px] font-medium text-gray-900">
            <FormattedMessage id="component.automationReferenceModal.confirmQuestion" defaultMessage="Are you sure you want to delete this {resourceLabel}?" values={{ resourceLabel }} />
          </div>
          <div className="text-sm text-gray-800 mt-[4px]">
            <FormattedMessage id="component.automationReferenceModal.confirmSubtext" defaultMessage="This will permanently delete the {resourceLabel}." values={{ resourceLabel }} />
          </div>
        </div>
      }
      showDescriptionChildren={true}
      cancelButtonText={<FormattedMessage id="component.automationReferenceModal.cancel" defaultMessage="Discard" />}
      buttons={[
        {
          label: <FormattedMessage id="component.automationReferenceModal.confirm" defaultMessage="Delete {resourceLabel}" values={{ resourceLabel }} />,
          action: "danger",
          icon: <IconTrash size={16} />,
          disabled: submitting || (hasWillDeactivate && !acknowledged),
          onClick: handleConfirm,
          dataAttribute: "automation-reference-delete"
        }
      ]}
    >
      <AutomationReferenceWarning
        resourceLabel={resourceLabel}
        willDeactivate={willDeactivate}
        mayBreak={mayBreak}
        acknowledged={acknowledged}
        setAcknowledged={setAcknowledged}
      />
    </ModalBody>
  );
};

export default AutomationReferenceModal;
